import { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

import EditableTable from "./partials/table";

import NewEmployeeModal from "./partials/NewEmployeeModal";
import SkeletonEmployees from "./skeletons/SkeletonEmployees";

const Employees = () => {
  const {
    getUsers,
    employeeList,
    managerList,
    uploadUsersHandler,
    notify,
    getLanguages,
  } = useOutletContext();

  const { t } = useTranslation();

  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [languages, setLanguages] = useState([]);

  const loadLanguages = useCallback(async () => {
    try {
      const result = await getLanguages();
      if (result) {
        setLanguages(result);
      }
    } catch (error) {
      console.error("Error loading languages:", error);
    }
  }, [getLanguages]);

  useEffect(() => {
    // get the list of employees and managers
    getUsers();
    loadLanguages();
  }, [getUsers, loadLanguages]);

  const fields = useMemo(
    () => [
      { key: "first_name", text: t("field.first_name"), editable: true },
      { key: "last_name", text: t("field.last_name"), editable: true },
      { key: "email", text: t("field.email"), editable: true },
      {
        key: "manager_user_ids",
        text: t("field.manager"),
        inputType: "select",
        editable: true,
        options:
          managerList
            ?.sort((a, b) => {
              return a.last_name.localeCompare(b.last_name);
            })
            .map(({ first_name, last_name, id }) => ({
              display: `${first_name} ${last_name}`,
              id,
            })) || [],
      },
      {
        key: "language",
        text: t("field.language"),
        inputType: "select",
        singleSelect: true,
        editable: true,
        options:
          languages?.data?.length > 0
            ? languages.data
                .sort((a, b) => {
                  return (a.attributes?.name || "").localeCompare(
                    b.attributes?.name || ""
                  );
                })
                .map((lang) => ({
                  id: lang.id,
                  display: lang.attributes?.name || lang.name || "",
                }))
            : [],
      },
    ],
    [managerList, languages, t]
  );

  const handleEmployeeAdd = () => {
    setIsEmployeeModalOpen(true);
  };

  const handleEmployeeModalClose = () => {
    setIsEmployeeModalOpen(false);
  };

  const handleEmployeeModalSubmit = async (newEmployeeData) => {
    try {
      const result = await uploadUsersHandler(
        [newEmployeeData],
        "employee",
        true
      );

      let addedEmployee = false;
      let errors = "Employee could not be added";

      let failedUploadedArray = result?.data?.failedUploaded;

      if (failedUploadedArray?.length === 1) {
        // determine if the error is an array or string
        if (typeof failedUploadedArray[0]?.errors === "string") {
          errors = failedUploadedArray[0]?.errors;
        }
      } else if (failedUploadedArray?.length === 0) {
        // if there is no failedUploaded array, then the employee was added successfully
        addedEmployee = true;
      }

      if (addedEmployee) {
        getUsers();
        notify("Employee added successfully", "success");
        setIsEmployeeModalOpen(false);
      } else {
        notify(errors, "error");
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <EditableTable
        SkeletonComponent={SkeletonEmployees}
        dataList={employeeList}
        role="employees"
        fields={fields}
        handleAdduser={handleEmployeeAdd}
      />

      <NewEmployeeModal
        isEmployeeModalOpen={isEmployeeModalOpen}
        handleEmployeeModalClose={handleEmployeeModalClose}
        handleEmployeeModalSubmit={handleEmployeeModalSubmit}
        managerList={managerList}
        mode={"new"}
        languages={languages}
      />
    </>
  );
};

export default Employees;
